import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Commune } from 'src/app/utils/interfaces/new/commune.interface';

@Injectable({
  providedIn: 'root'
})
export class ApiMetadataService {

  public apiUrl = 'https://api.insee.fr/metadonnees/V2';
  
  constructor(private http: HttpClient) { }
  
  private mapToCommune(item: any): Commune {
    return {
      nom: item.intitule || item.nom,
      code: item.code,
      codesPostaux: [],
      departement: {
        code: '',
        nom: ''
      }
    } as Commune;
  }

  // Méthode pour rechercher les communes par nom en fonction de la date de naisance
  searchCommunesByName(name: string, date?: string): Observable<Commune[]> {
    let url = `${this.apiUrl}/geo/communes?filtreNom=${encodeURIComponent(name)}`;

    if (date) {
      url += `&date=${encodeURIComponent(date)}`;
    }

    const headers = { 'Accept': 'application/json' };

    return this.http.get<any[]>(url, { headers }).pipe(
      switchMap(communes => this.addArrondissements(communes, headers)),
      map(communes => communes.map(c => this.mapToCommune(c))),
      catchError(err => throwError(() => err))
    );
  }

  // Lance en parallèle la recherche d'arrondissements pour chaque commune de la liste
  private addArrondissements(communes: any[], headers: any): Observable<any[]> {
    if (!communes?.length) {
      return of([]);
    }

    const requests = communes.map(c => this.getCommuneAndArrondissements(c, headers));

    return forkJoin(requests).pipe(
      map(results => results.reduce((acc, val) => acc.concat(val), []))
    );
  }

  // Si la commune est Paris, Lyon ou Marseille, récupère et ajoute ses arrondissements
  private getCommuneAndArrondissements(commune: any, headers: any): Observable<any[]> {
    const codes = ['75056', '69123', '13055'];

    if (!codes.includes(commune.code)) {
      return of([commune]);
    }

    const url = `${this.apiUrl}/geo/commune/${commune.code}/descendants?type=ArrondissementMunicipal`;

    return this.http.get<any[]>(url, { headers }).pipe(
      map(arrondissements => [
        commune,
        ...(arrondissements || [])
      ]),
      catchError(() => of([commune]))
    );
  }
  
  private paysCache: any[] = null;

  // Méthode pour rechercher les pays (exigence INS)
  searchPaysByName(name: string): Observable<any[]> {
    const headers = { 'Accept': 'application/json' };
    
    if (this.paysCache) {
      const term = name.toLowerCase();
      return of(this.paysCache.filter(p => p.intitule.toLowerCase().includes(term)));
    }

    return this.http.get<any[]>(`${this.apiUrl}/geo/pays`, { headers }).pipe(
      map(pays => {
        this.paysCache = pays;
        const term = name.toLowerCase();
        return pays.filter(p => p.intitule.toLowerCase().includes(term));
      }),
      catchError(() => of([]))
    );
  }
  
  private domTomCommunesCache: Commune[] = null;

  // Méthode pour rechercher les communes d'outre-mer (DOM-TOM) par nom ou code INSEE
  searchCollectivitesDOutreMer(searchTerm: string): Observable<Commune[]> {
    if (this.domTomCommunesCache) {
      return of(this.filterDomTomCache(searchTerm));
    }

    const headers = { 'Accept': 'application/json' };

    return this.http.get<any[]>(`${this.apiUrl}/geo/collectivitesDOutreMer`, { headers }).pipe(
      switchMap(collectivites => this.getAllCommunesDOutreMer(collectivites, headers)),
      map(allCommunes => {
        this.domTomCommunesCache = allCommunes.map(c => this.mapToCommune(c));
        return this.filterDomTomCache(searchTerm);
      }),
      catchError(err => throwError(() => err))
    );
  }

  // Lance en parallèle la recherche des communes pour chaque collectivité d'outre-mer
  private getAllCommunesDOutreMer(collectivites: any[], headers: any): Observable<any[]> {
    if (!collectivites?.length) {
      return of([]);
    }

    const requests = collectivites.map(col => 
      this.http.get<any[]>(`${this.apiUrl}/geo/collectiviteDOutreMer/${col.code}/descendants?type=Commune`, { headers }).pipe(
        catchError(() => of([]))
      )
    );

    return forkJoin(requests).pipe(
      map(results => results.reduce((acc, val) => acc.concat(val), []))
    );
  }

  // Filtre le cache des communes DOM-TOM par nom ou code INSEE
  private filterDomTomCache(searchTerm: string): Commune[] {
    const term = searchTerm.toLowerCase();
    return this.domTomCommunesCache.filter(c => 
      c.nom.toLowerCase().includes(term) || 
      c.code.includes(term)
    );
  }
  
  // Méthode pour rechercher les communes par code INSEE (utilisée pour cpNaissance)
  searchCommunesByInseeCode(inseeCode: string): Observable<Commune[]> {
    const headers = { 'Accept': 'application/json' };
    return this.http.get<any>(`${this.apiUrl}/geo/commune/${encodeURIComponent(inseeCode)}`, { headers }).pipe(
      map(res => {
        // L'API renvoie un seul objet pour un code INSEE spécifique, 
        // on l'emballe dans un tableau pour NgFor.
        return res ? [this.mapToCommune(res)] : [];
      }),
      catchError(err => throwError(() => err))
    );
  }

}
